import React, { useEffect, useState, useContext } from 'react';
import { useAuth } from 'react-oidc-context';
import { ButtonBase } from '@mui/material';
import { SnackbarContext } from '../../App';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

function OutlookSettings() {
    const auth = useAuth();
    const { sessionExpired } = useContext(SnackbarContext);

    const [isConnected, setIsConnected] = useState(false);
    const [loading, setLoading] = useState(true);
    const [connecting, setConnecting] = useState(false);

    // ── Check if client has connected Outlook ─────────────────
    function checkConnection() {
        fetch(`${window.origin}/api/smtp/auth/access-check`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${auth.user?.access_token}`
            }
        }).then(response => {
            if (response.status === 403) {
                sessionExpired(true);
                throw Error('Not Authorized');
            }
            setIsConnected(response.status === 200);
            setLoading(false);
        }).catch((error) => {
            console.error(error);
            setLoading(false);
        });
    }

    // ── Set ssid cookie then open OAuth popup ──────────────────
    function handleConnect() {
        setConnecting(true);

        // Step 1 — set the ssid cookie so the callback knows who is connecting
        fetch(`${window.origin}/api/smtp/auth/set-cookie`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${auth.user?.access_token}`
            },
            credentials: 'include',
        }).then(response => {
            if (response.status === 403) {
                sessionExpired(true);
                throw Error('Not Authorized');
            }
            return response.json();
        }).then(() => {
            // Step 2 — open Microsoft login popup
            const popup = window.open(
                `${window.origin}/api/smtp/auth/login`,
                'Connect Outlook',
                'width=500,height=600,scrollbars=yes'
            );

            // Step 3 — listen for success message from popup
            const messageListener = (event) => {
                if (event.data === 'smtp-auth-success') {
                    popup?.close();
                    window.removeEventListener('message', messageListener);
                    setConnecting(false);
                    checkConnection(); // refresh status
                }
            };
            window.addEventListener('message', messageListener);

            // Step 4 — fallback if popup is closed manually
            const popupTimer = setInterval(() => {
                if (popup?.closed) {
                    clearInterval(popupTimer);
                    window.removeEventListener('message', messageListener);
                    setConnecting(false);
                    checkConnection();
                }
            }, 1000);
        }).catch((error) => {
            console.error(error);
            setConnecting(false);
        });
    }

    // ── Disconnect Outlook ─────────────────────────────────────
    function handleDisconnect() {
        fetch(`${window.origin}/api/smtp/auth/disconnect`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${auth.user?.access_token}`
            }
        }).then(response => {
            if (response.status === 403) {
                sessionExpired(true);
                throw Error('Not Authorized');
            }
            return response.json();
        }).then(() => {
            setIsConnected(false);
        }).catch((error) => {
            console.error(error);
        });
    }

    useEffect(() => {
        checkConnection();
    }, []);

    return (
        <div>
            <p style={{ fontSize: '0.8vw', color: 'rgb(0, 15, 30, 0.6)', fontWeight: '300', textAlign: 'center' }}>
                Connect your Outlook account to send ATO communications from your own email address.
            </p>

            <div style={{ width: '100%', padding: '0.05vw 2.5vw' }}>
                <ButtonBase
                    style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: 'rgb(254, 254, 255, 0.9)',
                        padding: '1vw 1.5vw',
                        borderRadius: '1vw',
                        marginTop: '1.5vw',
                        marginBottom: '1.5vw',
                        width: '100%',
                        alignItems: 'flex-start',
                        cursor: 'default',
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'row', width: '100%', alignItems: 'center' }}>
                        
                        {/* Icon + Title */}
                        <div style={{ flex: 1 }}>
                            <p style={{ marginBottom: '0.4vw', fontSize: '0.9vw', color: 'rgb(0, 15, 30, 0.6)', textAlign: 'left', marginTop: '0.5vw' }}>
                                <MailOutlineIcon style={{ marginRight: '0.5vw', color: '#1c6c6c', fontSize: '1vw' }} />
                                Outlook Account
                            </p>
                            <p style={{ paddingBottom: '0.5vw', color: 'rgb(0, 15, 30, 0.9)', margin: 0, fontSize: '1.2vw', fontWeight: '400', letterSpacing: '0.05vw' }}>
                                Microsoft Outlook
                            </p>
                            <p style={{ fontSize: '0.7vw', fontWeight: '300', color: 'rgb(0, 15, 30, 0.7)', marginBottom: '0.5vw' }}>
                                ATO communications will be sent from your connected Outlook address.
                            </p>
                        </div>

                        {/* Status + Button */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5vw' }}>
                            
                            {/* Status badge */}
                            {!loading && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.3vw',
                                    fontSize: '0.75vw',
                                    color: isConnected ? '#1c6c6c' : 'rgb(0, 15, 30, 0.4)',
                                    marginBottom: '0.5vw'
                                }}>
                                    {isConnected
                                        ? <><CheckCircleOutlineIcon style={{ fontSize: '1vw' }} /> Connected</>
                                        : <><ErrorOutlineIcon style={{ fontSize: '1vw' }} /> Not connected</>
                                    }
                                </div>
                            )}

                            {/* Connect / Disconnect button */}
                            {!loading && (
                                <ButtonBase
                                    onClick={isConnected ? handleDisconnect : handleConnect}
                                    disabled={connecting}
                                    style={{
                                        backgroundColor: isConnected ? 'rgb(220, 50, 50, 0.1)' : '#1c6c6c',
                                        color: isConnected ? 'rgb(220, 50, 50, 0.8)' : 'white',
                                        padding: '0.4vw 1vw',
                                        borderRadius: '0.5vw',
                                        fontSize: '0.8vw',
                                        fontWeight: '500',
                                        opacity: connecting ? 0.6 : 1,
                                    }}
                                >
                                    {connecting ? 'Connecting...' : isConnected ? 'Disconnect' : 'Connect Outlook'}
                                </ButtonBase>
                            )}

                            {loading && (
                                <p style={{ fontSize: '0.8vw', color: 'rgb(0, 15, 30, 0.4)' }}>Checking...</p>
                            )}
                        </div>
                    </div>
                </ButtonBase>
            </div>
        </div>
    );
}

export default OutlookSettings;